import { useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api.js";
import { formatBytes } from "../../lib/format.js";
import { AdminTabs } from "../../components/AdminTabs.js";
import { PageSpinner } from "../../components/ui.js";

interface SystemInfo {
  status: string;
  uptimeSeconds: number;
  nodeVersion: string;
  counts: {
    users: number;
    activeAdmins: number;
    pendingInvitations: number;
    auditEntries: number;
  };
  storage: { databaseBytes: number; uploadsBytes: number; totalBytes: number };
  ai: { anyRealProvider: boolean; providerCount: number };
}

function uptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function AdminSystemPage() {
  const [info, setInfo] = useState<SystemInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setInfo(await api.get<SystemInfo>("/api/admin/system"));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Could not load system info.");
      }
    })();
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1>Administration</h1>
        <p className="subtitle">System health &amp; storage.</p>
      </div>
      <AdminTabs />

      {error && <div className="alert alert-danger">{error}</div>}
      {!info ? (
        <PageSpinner />
      ) : (
        <div className="stack">
          <div className="stat-grid">
            <div className="stat">
              <div className="stat-label">Status</div>
              <div className="stat-value" style={{ color: "var(--success)" }}>
                {info.status === "ok" ? "Healthy" : info.status}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Uptime</div>
              <div className="stat-value">{uptime(info.uptimeSeconds)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Users</div>
              <div className="stat-value">{info.counts.users}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Pending invites</div>
              <div className="stat-value">{info.counts.pendingInvitations}</div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Storage</span>
            </div>
            <div className="table-card" style={{ border: "none", padding: 0 }}>
              <div className="tc-row">
                <span className="tc-label">Database</span>
                <span className="mono">{formatBytes(info.storage.databaseBytes)}</span>
              </div>
              <div className="tc-row">
                <span className="tc-label">Uploads (private files)</span>
                <span className="mono">{formatBytes(info.storage.uploadsBytes)}</span>
              </div>
              <div className="tc-row" style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "var(--space-2)" }}>
                <span className="tc-label">Total on volume</span>
                <span className="mono" style={{ fontWeight: 600 }}>
                  {formatBytes(info.storage.totalBytes)}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">Runtime</span>
            </div>
            <div className="table-card" style={{ border: "none", padding: 0 }}>
              <div className="tc-row">
                <span className="tc-label">Node</span>
                <span className="mono">{info.nodeVersion}</span>
              </div>
              <div className="tc-row">
                <span className="tc-label">Active administrators</span>
                <span className="mono">{info.counts.activeAdmins}</span>
              </div>
              <div className="tc-row">
                <span className="tc-label">Audit entries</span>
                <span className="mono">{info.counts.auditEntries}</span>
              </div>
              <div className="tc-row">
                <span className="tc-label">AI providers configured</span>
                <span className="mono">
                  {info.ai.anyRealProvider ? info.ai.providerCount : "none (offline mock)"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
